/**
 * EmptyState illustration set — abstract geometric spot illustrations.
 *
 * Design contract (WS-2):
 *   - Pure inline SVG, zero image assets, zero deps.
 *   - Two-to-three tone compositions built ENTIRELY from `currentColor` +
 *     semantic token classes — never a raw hex. The wrapper sets the base
 *     tone via `text-primary`; the SVG derives lighter tints with opacity
 *     (`fill-current opacity-15` etc.) and a single warm secondary accent
 *     via a token color class on a child (`text-warning` / `text-info` /
 *     `text-success`), which flows into its own `fill`/`stroke` `currentColor`
 *     through a nested `<g className>`. This keeps both light + DARK theme
 *     correct automatically (tokens cascade) and stays tenant-brandable
 *     (primary follows `--color-primary`).
 *   - Decorative only: `aria-hidden`, `focusable={false}`, no `role`. The
 *     EmptyState text carries all semantics (WCAG 1.1.1 — the label is the
 *     accessible content, the art is presentational).
 *   - RTL-safe: compositions are mirror-neutral (no arrows, no text, no
 *     left/right-meaningful content) so they read identically in `dir="rtl"`
 *     without flipping.
 *   - No faces, no clip-art — refined editorial abstraction: overlapping
 *     rounded rects, circles, arcs.
 *
 * Sizing: each renders in a 160×120 viewBox and scales to the CSS box the
 * consumer sizes via `className` (EmptyState passes ~128–160px). Stroke widths
 * are chosen for the default size; `vector-effect="non-scaling-stroke"` keeps
 * them crisp when scaled down for `compact`.
 */
import * as React from "react";
import { cn } from "../../utils";

/** Named illustration keys — the `illustration` prop union on EmptyState. */
export type IllustrationName =
  | "people"
  | "calendar"
  | "money"
  | "documents"
  | "inbox-zero"
  | "search";

export interface IllustrationProps {
  /** Forwarded to the outer `<svg>` for sizing (EmptyState sets width/height). */
  className?: string;
}

/**
 * Shared `<svg>` shell — fixes the viewBox, marks the art decorative, and
 * seeds the base tone as `currentColor` (the consumer's `text-*` class). All
 * children draw with `fill="currentColor"` / `stroke="currentColor"` unless a
 * nested `<g>` overrides the color via a secondary token class.
 */
function Frame({
  className,
  children,
}: {
  className?: string | undefined;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 160 120"
      className={cn("h-auto w-full", className)}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/**
 * PEOPLE — overlapping rounded cards + orbiting circles suggesting a roster of
 * teammates, without drawing faces. Base primary tint + a warm accent chip.
 */
export function PeopleIllustration({ className }: IllustrationProps): React.JSX.Element {
  return (
    <Frame className={className}>
      {/* soft ground */}
      <rect x="18" y="86" width="124" height="10" rx="5" className="fill-current opacity-10" />
      {/* back card */}
      <rect x="30" y="30" width="56" height="56" rx="14" className="fill-current opacity-15" />
      {/* front card */}
      <rect x="72" y="42" width="56" height="44" rx="14" className="fill-current opacity-25" />
      {/* head + shoulder abstraction on the back card */}
      <circle cx="58" cy="50" r="9" className="fill-current opacity-60" />
      <path
        d="M44 78c0-9 6.3-16 14-16s14 7 14 16"
        className="stroke-current opacity-60"
        strokeWidth="4"
        strokeLinecap="round"
      />
      {/* warm accent teammate on the front card */}
      <g className="text-warning">
        <circle cx="100" cy="60" r="7" className="fill-current" />
        <path
          d="M90 82c0-7 4.5-12.5 10-12.5S110 75 110 82"
          className="stroke-current"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
      </g>
    </Frame>
  );
}

/**
 * CALENDAR — a month grid card with a highlighted day chip. Reads as
 * scheduling / leaves / holidays. Accent day uses the info token.
 */
export function CalendarIllustration({ className }: IllustrationProps): React.JSX.Element {
  return (
    <Frame className={className}>
      {/* card */}
      <rect x="34" y="26" width="92" height="76" rx="12" className="fill-current opacity-15" />
      {/* header bar */}
      <rect x="34" y="26" width="92" height="18" rx="12" className="fill-current opacity-30" />
      {/* binder rings */}
      <rect x="52" y="20" width="6" height="14" rx="3" className="fill-current opacity-50" />
      <rect x="102" y="20" width="6" height="14" rx="3" className="fill-current opacity-50" />
      {/* grid dots — 3 rows × 4 cols */}
      {[0, 1, 2].map((r) =>
        [0, 1, 2, 3].map((c) => (
          <circle
            key={`${r}-${c}`}
            cx={48 + c * 22}
            cy={58 + r * 15}
            r="3.5"
            className="fill-current opacity-40"
          />
        )),
      )}
      {/* highlighted day chip (info accent) */}
      <g className="text-info">
        <rect x="86" y="66" width="18" height="18" rx="6" className="fill-current" />
      </g>
    </Frame>
  );
}

/**
 * MONEY — stacked coins + a payslip card corner. Reads as payroll / loans /
 * expenses. Coins use the success token as the warm accent.
 */
export function MoneyIllustration({ className }: IllustrationProps): React.JSX.Element {
  return (
    <Frame className={className}>
      {/* ground */}
      <rect x="24" y="90" width="112" height="10" rx="5" className="fill-current opacity-10" />
      {/* document behind the coins */}
      <rect x="30" y="28" width="58" height="64" rx="10" className="fill-current opacity-15" />
      <rect x="40" y="40" width="30" height="5" rx="2.5" className="fill-current opacity-40" />
      <rect x="40" y="52" width="38" height="5" rx="2.5" className="fill-current opacity-30" />
      <rect x="40" y="64" width="24" height="5" rx="2.5" className="fill-current opacity-30" />
      {/* stacked coins (success accent) */}
      <g className="text-success">
        <ellipse cx="106" cy="82" rx="26" ry="9" className="fill-current opacity-90" />
        <ellipse cx="106" cy="70" rx="26" ry="9" className="fill-current opacity-70" />
        <ellipse cx="106" cy="58" rx="26" ry="9" className="fill-current" />
        {/* coin face mark */}
        <circle cx="106" cy="58" r="4" className="fill-surface opacity-90" />
      </g>
    </Frame>
  );
}

/**
 * DOCUMENTS — fanned pages with a folded corner + text lines. Reads as
 * documents / reports / files.
 */
export function DocumentsIllustration({ className }: IllustrationProps): React.JSX.Element {
  return (
    <Frame className={className}>
      {/* back page (rotated feel via offset) */}
      <rect
        x="44"
        y="24"
        width="64"
        height="80"
        rx="10"
        className="fill-current opacity-15"
        transform="rotate(-8 76 64)"
      />
      {/* front page */}
      <rect x="52" y="26" width="64" height="80" rx="10" className="fill-current opacity-25" />
      {/* folded corner */}
      <path d="M100 26h16l-16 16z" className="fill-current opacity-45" />
      {/* text lines */}
      <rect x="62" y="46" width="40" height="5" rx="2.5" className="fill-current opacity-50" />
      <rect x="62" y="58" width="44" height="5" rx="2.5" className="fill-current opacity-35" />
      <rect x="62" y="70" width="30" height="5" rx="2.5" className="fill-current opacity-35" />
      {/* accent tag (info) */}
      <g className="text-info">
        <circle cx="106" cy="90" r="9" className="fill-current" />
      </g>
    </Frame>
  );
}

/**
 * INBOX-ZERO / DONE — a settled tray with a calm check arc above it. This is
 * the "all caught up" moment: distinct from first-use (which uses a subject
 * illustration). The check uses the success token; no motion (static art).
 */
export function InboxZeroIllustration({ className }: IllustrationProps): React.JSX.Element {
  return (
    <Frame className={className}>
      {/* tray body */}
      <path
        d="M34 62h20l6 12h40l6-12h20v22a10 10 0 0 1-10 10H44a10 10 0 0 1-10-10z"
        className="fill-current opacity-18"
      />
      {/* tray lip */}
      <rect x="34" y="58" width="92" height="8" rx="4" className="fill-current opacity-30" />
      {/* calm check inside a soft disc (success accent) */}
      <g className="text-success">
        <circle cx="80" cy="38" r="20" className="fill-current opacity-20" />
        <path
          d="M71 38l6 6 12-13"
          className="stroke-current"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </Frame>
  );
}

/**
 * SEARCH / FILTERED — a magnifier over a faint result grid. Reads as
 * "no matches for this filter", distinct from "nothing exists yet".
 */
export function SearchIllustration({ className }: IllustrationProps): React.JSX.Element {
  return (
    <Frame className={className}>
      {/* faint result rows behind the lens */}
      <rect x="30" y="34" width="70" height="8" rx="4" className="fill-current opacity-12" />
      <rect x="30" y="50" width="54" height="8" rx="4" className="fill-current opacity-12" />
      <rect x="30" y="66" width="62" height="8" rx="4" className="fill-current opacity-12" />
      <rect x="30" y="82" width="40" height="8" rx="4" className="fill-current opacity-12" />
      {/* magnifier */}
      <circle
        cx="98"
        cy="58"
        r="26"
        className="stroke-current opacity-40"
        strokeWidth="6"
        fill="none"
      />
      <circle cx="98" cy="58" r="18" className="fill-current opacity-15" />
      {/* handle — diagonal, direction-neutral in RTL */}
      <rect
        x="114"
        y="74"
        width="10"
        height="26"
        rx="5"
        className="fill-current opacity-45"
        transform="rotate(-45 119 87)"
      />
      {/* accent dot inside the lens (warning) */}
      <g className="text-warning">
        <circle cx="98" cy="58" r="5" className="fill-current" />
      </g>
    </Frame>
  );
}

/** Registry mapping the named key → its component (used by EmptyState v2). */
export const ILLUSTRATIONS: Record<
  IllustrationName,
  (props: IllustrationProps) => React.JSX.Element
> = {
  people: PeopleIllustration,
  calendar: CalendarIllustration,
  money: MoneyIllustration,
  documents: DocumentsIllustration,
  "inbox-zero": InboxZeroIllustration,
  search: SearchIllustration,
};
